import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Search, MapPin, Briefcase } from 'lucide-react';
import { stats } from '../mock/mockData'; 

const Hero = () => {
  const [city, setCity] = useState('');
  const [role, setRole] = useState('');
  const [dynamicCities, setDynamicCities] = useState([]);
  
  const [allJobs, setAllJobs] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchHeroData = async () => {
      try {
        const [cityRes, jobsRes] = await Promise.all([
          api.get("/nav-filters"),
          api.get("/public-jobs")
        ]);
        setDynamicCities(cityRes.data.cities || []);
        setAllJobs(Array.isArray(jobsRes.data) ? jobsRes.data : []);
      } catch (error) {
        console.error("Error fetching data for hero section", error);
      }
    };
    fetchHeroData();

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setRole(val);

    if (val.trim().length > 0) {
      const filtered = allJobs.filter(job =>
        (job.title && job.title.toLowerCase().includes(val.toLowerCase())) ||
        (job.company && job.company.toLowerCase().includes(val.toLowerCase())) ||
        (job.category && job.category.toLowerCase().includes(val.toLowerCase()))
      );

      const uniqueTitles = [...new Set(filtered.map(j => j.title || j.category).filter(Boolean))].slice(0, 5);
      setSuggestions(uniqueTitles);
      setShowDropdown(uniqueTitles.length > 0);
    } else {
      setShowDropdown(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setRole(suggestion);
    setShowDropdown(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setShowDropdown(false);

    let queryParams = new URLSearchParams();
    if (city) queryParams.append('city', city);
    if (role) queryParams.append('skill', role);

    navigate(`/job-listing?${queryParams.toString()}`);
  };

  return (
    <section
      className="relative transition-colors duration-300 overflow-visible"
      style={{ background: 'linear-gradient(135deg, #060606ff 0%, #393a3dff 55%, #8f929aff 100%)' }}
    >
      <div className="absolute inset-0 opacity-20 pointer-events-none overflow-hidden">
        <div className="absolute -top-16 -left-16 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute top-20 right-10 w-96 h-96 rounded-full bg-[#FED547]/20 blur-3xl" />
        <div className="absolute bottom-10 left-1/3 w-80 h-80 rounded-full bg-blue-300/10 blur-3xl" />
      </div>

      <div className="relative overflow-visible max-w-[1200px] mx-auto px-4 pt-14 pb-20 text-center">
        <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-xs backdrop-blur-sm">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          India's No.1 Blue Collar & Entry-Level Job Portal
        </div>

        <h1 className="text-white font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight">
          Find the best job near you
        </h1>

        <p className="text-orange-200 font-medium mt-3 text-lg">
          <span className="text-white font-bold">{stats.vacancies}</span> active job vacancies to grab
        </p>

        <form onSubmit={handleSearch} className="mt-10 max-w-3xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg md:rounded-full shadow-2xl p-2 flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-0 transition-colors duration-300 relative">
            
            {/* City Dropdown */}
            <div className="flex items-center gap-2 flex-1 px-4 py-2 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700 transition-colors duration-300 w-full">
              <MapPin className="w-5 h-5 text-[#000000] shrink-0 dark:text-white" />
              <select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full text-sm text-gray-700 dark:text-white bg-transparent outline-none appearance-none cursor-pointer"
              >
                <option value="">Select City</option>
                {dynamicCities.map((c, index) => (
                  <option key={index} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Dynamic Search Input with Upward Modal Dropdown */}
            <div ref={dropdownRef} className="relative flex items-center gap-2 flex-1 px-4 py-2 transition-colors duration-300 w-full">
              <Briefcase className="w-5 h-5 text-[#000000] shrink-0 dark:text-white" />
              <input
                type="text"
                value={role}
                onChange={handleInputChange}
                onFocus={() => { if (role.trim() && suggestions.length > 0) setShowDropdown(true); }}
                placeholder="Enter job role, skill or company"
                autoComplete="off"
                className="w-full text-sm text-gray-700 dark:text-white bg-transparent outline-none placeholder:text-gray-400 dark:placeholder-gray-400"
              />

              {/* Suggestions Dropdown opening upwards (Modal style) */}
              {showDropdown && suggestions.length > 0 && (
                <div className="absolute left-0 right-0 bottom-[120%] mb-2 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden text-left z-[99999]">
                  <ul className="max-h-60 overflow-y-auto divide-y divide-gray-800">
                    {suggestions.map((suggestion, index) => (
                      <li
                        key={index}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSuggestionClick(suggestion);
                        }}
                        className="px-5 py-3.5 hover:bg-gray-800 cursor-pointer text-sm text-white flex items-center gap-3 transition-colors"
                      >
                        <Search className="w-4 h-4 text-yellow-400 shrink-0" />
                        <span className="font-medium">{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <button
              type="submit"
              className="flex items-center justify-center gap-2 bg-[#FED547] hover:bg-[#000000] hover:text-[#FED547] text-black font-bold px-6 md:px-8 py-3 rounded-md md:rounded-full transition-colors"
            >
              <Search className="w-4 h-4" />
              SEARCH
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Hero;